import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, AlertTriangle, BookOpen } from 'lucide-react';
import Tip from '../../components/Tip';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface WavePoint {
  wave: number;
  price: number;
  index: number;
  type: string;
}

interface WaveCount {
  current_wave: number | null;
  waves: WavePoint[];
  direction: 'bullish' | 'bearish' | null;
}

interface Projection {
  level: string;
  price: number;
}

interface TimeframeResult {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  wave_count: WaveCount;
  projections: { wave_targets: Projection[]; correction_targets: Projection[] };
  rules_valid: boolean;
  invalidation_level: number | null;
  entry_price: number | null;
  stop_loss: number | null;
  take_profits: { price: number; label: string }[];
  risk_reward: number | null;
  conviction: 'HIGH' | 'LOW' | null;
  optimal_entry: number | null;
  optimal_entry_label: string | null;
  sl_label: string | null;
  error?: string;
}

interface CoinResult {
  symbol: string;
  price: number;
  change24h: number;
  timeframes: Record<string, TimeframeResult>;
}

interface ElliottWaveTabProps {
  watchlist: WatchlistItem[];
  apiUrl?: string;
}

const TIMEFRAMES = ['1h', '4h', '1d'];

const ElliottWaveTab: React.FC<ElliottWaveTabProps> = ({ watchlist, apiUrl }) => {
  const [data, setData] = useState<CoinResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTf, setSelectedTf] = useState('1d');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const url = apiUrl || API_URL;

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) return;
    setLoading(true);
    try {
      const symbols = watchlist.map(w => w.pair).join(',');
      const response = await fetch(`${url}/api/signals/elliott?symbols=${symbols}`, {
        signal: AbortSignal.timeout(90000),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Elliott fetch error:', e);
    }
    setLoading(false);
  }, [watchlist, url]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nameMap: Record<string, string> = {};
  watchlist.forEach(w => { nameMap[w.symbol] = w.name; });

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return 'text-green-500';
    if (signal === 'SELL') return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalIcon = (signal: string) => {
    if (signal === 'BUY') return <TrendingUp className="w-5 h-5" />;
    if (signal === 'SELL') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="flex gap-1.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => setSelectedTf(tf)}
              className={`px-3 py-1.5 rounded-md text-sm font-mono font-semibold transition-all ${
                selectedTf === tf
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-gray-800 text-gray-500 border border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <span className="text-xs text-gray-400">Wave counting + Fibonacci projections</span>
        <a href="/guides/guide-elliott.html" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded text-xs font-medium transition-colors whitespace-nowrap">
          <BookOpen className="w-3.5 h-3.5" />
          How to read this
        </a>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 mb-3 p-2 bg-yellow-500/5 border border-yellow-500/10 rounded text-[11px] text-yellow-500/70">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Elliott Wave analysis is inherently subjective. Confidence capped at 70%. Use alongside other methods.
      </div>

      {lastRefresh && (
        <div style={{ marginBottom: 12, fontSize: 11, color: '#52525b' }}>
          Updated: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 2 min
        </div>
      )}

      {loading && data.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-400 text-sm">Detecting wave patterns...</span>
        </div>
      )}

      <div className="grid gap-5">
        {data.map(coin => {
          const tfData = coin.timeframes?.[selectedTf];
          if (!tfData) return null;
          const wc = tfData.wave_count;

          return (
            <div key={coin.symbol} className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{coin.symbol}-USD</h2>
                  <p className="text-gray-400 text-xs">{nameMap[coin.symbol] || coin.symbol}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-mono">${formatPrice(coin.price)}</div>
                  <div className={`text-xs ${coin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </div>
                </div>
              </div>

              {tfData.error && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {tfData.error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Signal + Wave Sequence */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Signal &amp; Wave <Tip text="Markets move in 5-wave impulses (with the trend) followed by 3-wave corrections (against it). BUY signals at Wave 2/4 pullbacks, SELL at Wave 5 completion." /></div>
                  <div className={`flex items-center gap-2 mb-3 ${getSignalColor(tfData.signal)}`}>
                    {getSignalIcon(tfData.signal)}
                    <span className="font-bold">{tfData.signal}</span>
                    <span className="ml-auto text-xs text-gray-400">Confidence <Tip text="Wave position confidence, capped at 70%. Higher when wave count follows all 3 rules and price is at a corrective wave (2 or 4)." /> <span className="font-mono">{tfData.confidence}%</span></span>
                  </div>

                  {/* Direction */}
                  {wc.direction && (
                    <div className={`text-xs px-2 py-0.5 rounded text-center mb-3 ${
                      wc.direction === 'bullish' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                    }`}>
                      {wc.direction.toUpperCase()} IMPULSE <Tip text="Bullish impulse: 5 waves moving upward (buy dips at waves 2 and 4). Bearish impulse: 5 waves moving downward (sell rallies at waves 2 and 4)." />
                    </div>
                  )}

                  {/* Wave sequence */}
                  {wc.waves.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap mb-2">
                      {[0, 1, 2, 3, 4, 5].map(w => {
                        const isPresent = wc.waves.some(wp => wp.wave === w);
                        const isCurrent = wc.current_wave === w;
                        return (
                          <div
                            key={w}
                            className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${
                              isCurrent
                                ? 'bg-blue-500 text-white'
                                : isPresent
                                ? 'bg-gray-700 text-white'
                                : 'bg-gray-800 text-gray-600'
                            }`}
                          >
                            {w === 0 ? 'S' : w}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Rules validity */}
                  <div className="flex items-center gap-1 text-xs">
                    <span className={tfData.rules_valid ? 'text-green-400' : 'text-yellow-400'}>
                      {tfData.rules_valid ? '\u2713 Rules Valid' : '\u26A0 Rules Violated'}
                    </span>
                    <Tip text="Three cardinal rules: (1) Wave 2 cannot retrace >100% of Wave 1, (2) Wave 3 cannot be the shortest impulse wave, (3) Wave 4 cannot overlap Wave 1 territory. If violated, the wave count is likely wrong." />
                  </div>

                  {/* Invalidation */}
                  {tfData.invalidation_level && (
                    <div className="mt-1 text-xs">
                      <span className="text-gray-500">Invalidation <Tip text="The price at which this wave count breaks. If price crosses this level, the count is wrong and should be restarted. Use this as your stop loss." />: </span>
                      <span className="font-mono text-red-400">${formatPrice(tfData.invalidation_level)}</span>
                    </div>
                  )}
                </div>

                {/* Projections (merged) */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Projections <Tip text="Fibonacci price targets. Wave targets project the next impulse wave (Wave 5 from Wave 4, or Wave 3 from Wave 2) using extensions of Wave 1 length. Correction levels show Fibonacci retracements for the current corrective wave depth." /></div>
                  {/* Wave Targets */}
                  {tfData.projections.wave_targets.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] text-green-400/60 uppercase mb-1">
                        {tfData.wave_count.current_wave === 4 ? 'Wave 5 Targets' : tfData.wave_count.current_wave && tfData.wave_count.current_wave <= 3 ? 'Wave 3 Targets' : 'Wave Targets'}
                      </div>
                      <div className="space-y-0.5">
                        {tfData.projections.wave_targets.map((t, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-gray-500">{t.level}</span>
                            <span className="font-mono text-green-400">${formatPrice(t.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Correction Targets */}
                  {tfData.projections.correction_targets.length > 0 && (
                    <div>
                      {tfData.projections.wave_targets.length > 0 && <div className="border-t border-gray-700 my-2" />}
                      <div className="text-[10px] text-yellow-400/60 uppercase mb-1">
                        {tfData.wave_count.current_wave && tfData.wave_count.current_wave >= 5 ? 'A-B-C Correction Targets' : `Wave ${tfData.wave_count.current_wave} Correction Levels`}
                      </div>
                      <div className="space-y-0.5">
                        {tfData.projections.correction_targets.map((t, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-gray-500">{t.level}</span>
                            <span className="font-mono text-yellow-400">${formatPrice(t.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {tfData.projections.wave_targets.length === 0 && tfData.projections.correction_targets.length === 0 && (
                    <div className="text-xs text-gray-600">No projections available</div>
                  )}
                </div>

                {/* Trade Setup */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 uppercase">Trade Setup <Tip text="Optimal Entry = the wave correction extreme (best R:R limit order). Current Entry = live market price. Stop loss at the nearest wave invalidation level. Take profit at the nearest Fibonacci projection target. Setup only shown when current entry still offers R:R ≥ 2.0." /></span>
                    {tfData.conviction === 'HIGH' && (
                      <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[10px] font-bold border border-green-500/20">HIGH CONVICTION</span>
                    )}
                    {tfData.conviction === 'LOW' && (
                      <span className="px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 rounded text-[10px] font-bold border border-yellow-500/20">LOW CONVICTION</span>
                    )}
                    {tfData.conviction && (
                      <Tip text="HIGH = R:R ≥ 2.0 + stop ≥ 1.5% from entry + Wave 2/4 entry + rules validated (confidence ≥ 50%). All four must align. LOW = signal fired but missing one or more: tight stop, rules violated, or unfavorable wave position." />
                    )}
                  </div>
                  {tfData.entry_price && tfData.risk_reward ? (
                    <div>
                      {/* R:R large display */}
                      <div className="text-center mb-3">
                        <div className={`text-3xl font-bold font-mono ${
                          tfData.risk_reward >= 3 ? 'text-green-400' : tfData.risk_reward >= 1.5 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {tfData.risk_reward.toFixed(1)}R
                        </div>
                        <div className="text-[10px] text-gray-500">Risk/Reward <span className="text-gray-600">(to TP1)</span></div>
                      </div>

                      <div className="space-y-2 text-xs">
                        {tfData.optimal_entry && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Optimal Entry <span className="text-gray-600">({tfData.optimal_entry_label})</span></span>
                            <span className="font-mono text-blue-400/50">${formatPrice(tfData.optimal_entry)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">Current Entry <span className="text-gray-600">(live price)</span></span>
                          <span className="font-mono text-blue-400">${formatPrice(tfData.entry_price)}</span>
                        </div>
                        {tfData.stop_loss && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Stop Loss {tfData.sl_label && <span className="text-gray-600">({tfData.sl_label})</span>}</span>
                            <span className="font-mono text-red-400">${formatPrice(tfData.stop_loss)}</span>
                          </div>
                        )}
                        {tfData.take_profits?.length > 0 && (
                          <div className="border-t border-gray-700 pt-2 mt-2 space-y-1.5">
                            {tfData.take_profits.map((tp, i) => (
                              <div key={i} className="flex justify-between">
                                <span className="text-gray-500">TP{i + 1} <span className="text-gray-600">({tp.label})</span></span>
                                <span className={`font-mono ${i === 0 ? 'text-green-400' : i === 1 ? 'text-green-400/70' : 'text-green-400/50'}`}>${formatPrice(tp.price)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-20">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">No active setup</div>
                        <div className="text-[10px] text-gray-700">Waiting for corrective wave entry</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ElliottWaveTab;
